import { apiFetch } from './data-service-api.js';

// ============================================================================
// worker-routing-registry.js — Single registry-style worker switch (Peter 2026-05-15)
// ----------------------------------------------------------------------------
// Replaces the per-feature flag explosion (qm_use_job_workers,
// qm_disable_harvest_worker, qm_use_work_order_draft_worker, + 9 future
// Phase 2c flags) with ONE central registry that maps task name → routing
// decision ('worker' | 'inline').
//
// Why registry-style over per-flag (Peter's design call):
//   1. ONE source of truth — flipping a default is a one-line change here,
//      not three sites (flag default in routed.js + DeveloperPage flag row
//      + the "experimental" tag).
//   2. Operator UX scales linearly: as Phase 2c adds 9 narrative-tool tasks,
//      the DeveloperPage shows one row per task with a dropdown
//      (worker / inline / A/B), not 9 new ON/OFF flag entries.
//   3. Hypothesis/experimentation hook: an 'A/B' state can split traffic
//      50/50 and surface telemetry, so we can MEASURE which routing wins
//      before committing to a default. (A/B routing is a future enhancement;
//      this module ships with the 'worker' | 'inline' MVP first.)
//   4. Old per-feature flags are still honoured during transition (back-
//      compat) — see resolveWorkerRouting() precedence chain below.
//
// Default seed (baked into ROUTING_DEFAULTS):
//   work_order_draft:  'inline'   — Phase 2b.5 default-OFF until proven
//   media_transcode:   'inline'   — qm_use_job_workers default-OFF for video
//
// Cross-references:
//   - feedback_branch_integration_discipline.md (CLAUDE.md HARD RULE)
//   - backend/src/services/job-scopes.js (worker scope definitions)
//   - docs/ai-pipeline-council/worker-container/PHASE-2-DESIGN.md
//   - Peter directive 2026-05-15 — "registry-style switch that you eventually
//     figure out which is better through experimentation and hypothesis"
// ============================================================================

/**
 * Default routing decisions per task. Single source of truth. Frozen so
 * mutation doesn't silently widen a routing change.
 *
 * To add a new worker task:
 *   1. Add the entry here with the chosen default.
 *   2. The corresponding routing-layer service (e.g. work-order-draft-routed.ts)
 *      calls resolveWorkerRouting('<task>') and dispatches accordingly.
 *   3. The DeveloperPage worker-routing surface auto-discovers the new task.
 *
 * Values:
 *   'worker' — submit to qm-worker queue (async, tab-close-safe)
 *   'inline' — run in the current tab (legacy/fallback path)
 *   (planned) 'ab-50-50' — split traffic for hypothesis measurement
 */
export const ROUTING_DEFAULTS = Object.freeze({
    // 2026-05-15 (v5.24.15): flipped 'inline' → 'worker' once the Final Quote
    // preview WorkOrderDraftPanel landed — the AI draft is now visible to
    // operators (fetchLatestWorkOrderDraft + panel render), so generating
    // one at WON is no longer dark spend. The deterministic builder still
    // runs unconditionally; this just adds the AI enrichment pass.
    work_order_draft: 'worker',
    media_transcode: 'inline',
});

/**
 * Valid routing values. Adding 'ab-*' variants here when the
 * experimentation framework lands.
 */
const VALID_ROUTES = Object.freeze(new Set(['worker', 'inline']));

/**
 * localStorage key per task. Reading + writing through these getters keeps
 * the convention discoverable from one place.
 */
function lsKey(task) {
    return `qm_worker_routing_${task}`;
}

/**
 * Legacy per-feature flag fallback table. Each entry maps the OLD flag name
 * to a function that returns the routing decision (`worker` | `inline` |
 * `null` for "this flag isn't expressing a preference"). Read in order; the
 * first non-null wins.
 *
 * Honoured during the transition window so anyone who manually set the old
 * flags pre-2026-05-15 keeps the same behaviour without re-configuring.
 *
 * Once we're confident no one's still on the old flags (sweep DevTools
 * usage telemetry over 30 days), remove this entirely.
 */
const LEGACY_FLAG_FALLBACKS = Object.freeze({
    work_order_draft: [
        // qm_use_work_order_draft_worker === 'true' → WORKER (opt-in)
        () => readLs('qm_use_work_order_draft_worker') === 'true' ? 'worker' : null,
    ],
    media_transcode: [
        // qm_use_job_workers === 'true' → WORKER (raises video size cap)
        () => readLs('qm_use_job_workers') === 'true' ? 'worker' : null,
    ],
});

function readLs(key) {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return window.localStorage.getItem(key);
        }
    } catch { /* unavailable */ }
    return null;
}

/**
 * Resolve the worker-routing decision for a task.
 *
 * Precedence (highest first):
 *   1. NEW localStorage key `qm_worker_routing_<task>` — if set to a valid
 *      route, that wins. Operators can explicitly override on any task.
 *   2. LEGACY per-feature flag (for back-compat; see LEGACY_FLAG_FALLBACKS).
 *      First flag returning a non-null wins.
 *   3. ROUTING_DEFAULTS[task] — the baked-in default.
 *
 * Throws if the task isn't registered in ROUTING_DEFAULTS (defensive —
 * forces callers to keep the registry in sync with their dispatch table).
 *
 * @param {string} task — registered task name (matches jobs.type)
 * @returns {'worker' | 'inline'} the routing decision
 */
export function resolveWorkerRouting(task) {
    if (!(task in ROUTING_DEFAULTS)) {
        throw new Error(
            `worker-routing-registry: unknown task "${task}". ` +
            `Add to ROUTING_DEFAULTS in src/services/worker-routing-registry.js. ` +
            `Known tasks: ${Object.keys(ROUTING_DEFAULTS).join(', ')}.`,
        );
    }

    // Step 1: explicit new-style localStorage override.
    const newOverride = readLs(lsKey(task));
    if (newOverride && VALID_ROUTES.has(newOverride)) {
        return newOverride;
    }

    // Step 2: legacy per-feature flag back-compat.
    const legacyFns = LEGACY_FLAG_FALLBACKS[task] || [];
    for (const fn of legacyFns) {
        const decision = fn();
        if (decision != null) return decision;
    }

    // Step 3: baked-in default.
    return ROUTING_DEFAULTS[task];
}

/**
 * Convenience: is the task routed to the worker right now?
 */
export function shouldUseWorker(task) {
    return resolveWorkerRouting(task) === 'worker';
}

/**
 * Operator override — set the routing for a task. UI helper for the
 * DeveloperPage worker-routing surface. Pass null to clear the override and
 * fall back to legacy flags + default.
 *
 * @param {string} task
 * @param {'worker' | 'inline' | null} route
 */
export function setWorkerRouting(task, route) {
    if (!(task in ROUTING_DEFAULTS)) {
        throw new Error(`worker-routing-registry: unknown task "${task}"`);
    }
    if (route != null && !VALID_ROUTES.has(route)) {
        throw new Error(`worker-routing-registry: invalid route "${route}". Must be 'worker', 'inline', or null.`);
    }
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            if (route == null) {
                window.localStorage.removeItem(lsKey(task));
            } else {
                window.localStorage.setItem(lsKey(task), route);
            }
        }
    } catch { /* unavailable; silent */ }
}

/**
 * List every registered task + the routing decision currently in effect.
 * The DeveloperPage uses this to render the worker-routing surface.
 *
 * @returns {Array<{task: string, default: string, current: string, override: string | null}>}
 */
export function listWorkerRouting() {
    return Object.keys(ROUTING_DEFAULTS).map(task => ({
        task,
        default: ROUTING_DEFAULTS[task],
        current: resolveWorkerRouting(task),
        override: readLs(lsKey(task)),
    }));
}

// ============================================================================
// DEC-COUNCIL-007 / Candidate C2 — submitJobRouted (consolidated submit)
// ----------------------------------------------------------------------------
// Single submit entry point that the *-routed shims delegate to. The
// per-task payload shape + log surface + validator live HERE so adding the
// 7+ planned narrative-tool tasks (quote_generation, costing, wms,
// risk_synthesis, quality_validation, method_first, narrative_tool) means one
// row in TASK_CONFIG + one row in ROUTING_DEFAULTS + the backend KNOWN_JOB_TYPES
// row — not a new file per task.
// ============================================================================

/**
 * Per-task config the consolidated submit relies on. Keys MUST match the
 * task names in ROUTING_DEFAULTS — `_assertConfigParity` enforces this at
 * module load.
 */
const TASK_CONFIG = Object.freeze({
    work_order_draft: {
        validator: (args) => {
            if (!args?.projectId) return 'projectId required';
            return null;
        },
        buildPayload: (args) => ({
            projectId: args.projectId,
            projectTitle: args.projectTitle || 'Untitled Project',
            phases: Array.isArray(args.phases) ? args.phases : [],
            overallNotes: args.overallNotes || '',
            wmsSummary: args.wmsSummary || '',
            model: args.model || '',
        }),
    },
    // media_transcode keeps its bespoke routed path for now.
});

/**
 * Boot-time schema-guard (Tsunade C2 condition 2): keys of TASK_CONFIG MUST
 * be a subset of ROUTING_DEFAULTS keys. A typo here silently weakens
 * validation for one task, so we fail-loud at import time.
 */
function _assertConfigParity() {
    const routingKeys = new Set(Object.keys(ROUTING_DEFAULTS));
    for (const task of Object.keys(TASK_CONFIG)) {
        if (!routingKeys.has(task)) {
            throw new Error(
                `worker-routing-registry: TASK_CONFIG references "${task}" which is not in ROUTING_DEFAULTS. ` +
                `Add to ROUTING_DEFAULTS or remove from TASK_CONFIG.`
            );
        }
    }
}
_assertConfigParity();

/**
 * Consolidated submit entry point — used by the four routed.js shims.
 *
 * Behaviour:
 *   - resolveWorkerRouting(task) === 'inline' → returns `{queued: false, reason: 'inline_path'}`.
 *     Caller falls back to its inline sibling.
 *   - validator(args) returns a string → `{queued: false, reason: 'invalid_args', error: <msg>}`.
 *   - apiFetch('/jobs/submit', …) succeeds → `{queued: true, jobId, status, jobToken?}`.
 *   - apiFetch throws → `{queued: false, reason: 'submit_failed', error: <msg>}`.
 *
 * @param {string} task — registered task name
 * @param {object} args — task-specific args (validator/buildPayload decide what's required)
 * @returns {Promise<{queued: boolean, jobId?: string, status?: string, jobToken?: string, reason?: string, error?: string}>}
 */
export async function submitJobRouted(task, args) {
    if (!(task in TASK_CONFIG)) {
        return {
            queued: false,
            reason: 'unregistered_task',
            error: `submitJobRouted: task "${task}" not in TASK_CONFIG. Known: ${Object.keys(TASK_CONFIG).join(', ')}`,
        };
    }

    let useWorker = false;
    try {
        useWorker = shouldUseWorker(task);
    } catch (err) {
        console.warn(`[QM] worker-routing-registry refused ${task} (${err?.message}); inline path`);
        return { queued: false, reason: 'registry_error', error: err?.message };
    }

    if (!useWorker) {
        return { queued: false, reason: 'inline_path' };
    }

    const { validator, buildPayload } = TASK_CONFIG[task];
    const validationError = validator(args);
    if (validationError) {
        return { queued: false, reason: 'invalid_args', error: validationError };
    }

    let payload;
    try {
        payload = buildPayload(args);
    } catch (err) {
        return { queued: false, reason: 'payload_build_failed', error: err?.message || 'unknown' };
    }

    try {
        const result = await apiFetch('/jobs/submit', {
            method: 'POST',
            body: JSON.stringify({ type: task, payload }),
        });
        console.log(
            `[QM] ${task} queued to worker — jobId=${result?.jobId} project=${args?.projectId || 'n/a'}`
        );
        return {
            queued: true,
            jobId: result?.jobId,
            status: result?.status || 'queued',
            jobToken: result?.jobToken,
        };
    } catch (err) {
        console.warn(
            `[QM] ${task} submit failed (${err?.message}); caller may fall back to inline path`
        );
        return { queued: false, reason: 'submit_failed', error: err?.message || 'unknown' };
    }
}

/**
 * Test-only: read the LS key name for a task (so tests don't need to
 * reconstruct the convention).
 */
export const _internals = Object.freeze({ lsKey, readLs, ROUTING_DEFAULTS, LEGACY_FLAG_FALLBACKS, TASK_CONFIG });
